import type { ReactNode } from 'react';
import React, { useCallback, useMemo } from 'react';
import { FilterGroup, FilterLabel, MultiToggleSwitch } from '@lt/components';
import styled from 'styled-components';
import type { CurrencyType } from '@lt/components/utils/currency';
import RadioFilterGroup from './RadioFilterGroup';
import type { IFilter } from '../types';

type LabelInfo = {
  LABEL: ReactNode;
  TOOLTIP: string;
};

interface MultiToggleFilterGroupProps extends Omit<IFilter, 'onClear'> {
  labels: Record<number, LabelInfo>;
  filterName: string;
  filterLabel: string;
  switcherIcon: React.ReactNode;
  labelIcon: React.ReactNode;
  currencyCode: CurrencyType;
}

const MultiToggleFilterGroup = ({
  onChange,
  switcherIcon,
  labelIcon,
  isMobile,
  className,
  data,
  labels,
  filterName,
  filterLabel,
  currencyCode,
}: MultiToggleFilterGroupProps) => {
  const handleChange = useCallback(
    (selectedId: number | string) => {
      const diff = data.map(({ id }) => ({
        id,
        selected: id === selectedId,
      }));
      onChange({ path: filterName, diff });
    },
    [filterName, onChange, data],
  );

  const options = useMemo(
    () =>
      data.map(({ selected, disabled, hotel_count, id, min_price }) =>
        isMobile
          ? {
              selected,
              disabled,
              hotel_count,
              id,
              name: labels[id].LABEL,
              min_price,
              currencyCode,
            }
          : {
              selected,
              disabled,
              id,
              labelComponent: (
                <StyledFilterLabel
                  count={hotel_count}
                  price={min_price}
                  name={labels[id].LABEL as string}
                  description={labels[id].TOOLTIP}
                  selected={selected}
                  disabled={disabled}
                  currencyCode={currencyCode}
                />
              ),
            },
      ),
    [data, isMobile, labels, currencyCode],
  );

  if (isMobile) {
    return (
      <RadioFilterGroup
        className={className}
        data={options}
        filterName={filterName}
        filterLabel={filterLabel}
        LabelComponent={labelIcon}
        onChange={({ id }) => handleChange(id)}
        isMobile
      />
    );
  }

  return (
    <FilterGroup
      label={
        <div>
          {filterLabel}
          {labelIcon}
        </div>
      }
    >
      <MultiToggleSwitch
        className={className}
        id={`filter-${filterName}`}
        icon={switcherIcon}
        onChange={handleChange}
        options={options}
      />
    </FilterGroup>
  );
};

const StyledFilterLabel = styled(FilterLabel)`
  * {
    white-space: normal;
  }
`;

export default MultiToggleFilterGroup;
